// 跨设备「接力专注」传输层（Handoff）。
//
// 一台设备开始专注后，会持续广播一份「live 快照」（剩余时间、标签、90/20 进度等）；
// 另一台设备 / 另一个标签页探测到他人正在进行的专注，便提示「在此继续」，接力剩余时间，
// 原设备随即静默让出（不结束后端会话，由新设备续传同一条会话的心跳）。
//
// 传输通道（多层并用，覆盖范围由窄到宽）：
//   1. BroadcastChannel —— 同源、同浏览器、跨标签/窗口即时投递（本地主通道）。
//   2. localStorage + storage 事件 —— BroadcastChannel 不可用时兜底，同样跨标签。
//   3. 后端共享快照 —— 用户维度共享，跨越「每个平台端」（桌面 Tauri / 移动 / Web
//      各自独立的进程与设备）。前两条都基于浏览器本地，只能桥接「同机多窗口 / PWA
//      多实例」；真正的跨端接力必须靠后端在用户维度共享这份快照。
//
// 后端通道由上层（Index.vue）通过构造参数 opts.backend 注入，本模块不直接依赖 api，
// 保持纯传输层、可单测。契约：
//   GET  /user/ActiveFocus         →  返回该用户当前活跃的 live 快照（其他端 ping 中），无则空
//   POST /user/PublishActiveFocus  →  上报/刷新本设备快照（刷新服务端 TTL）
//   POST /user/ClearActiveFocus    →  清除本设备快照
// 后端通道为「尽力而为」：仅登录态启用，连续失败（如端点未就绪）自动停用并只告警一次，
// 不影响本地通道，也不刷屏 console。

const CHANNEL = "zeno-handoff";
const STORAGE_KEY = "zeno_live_focus";
const DEVICE_KEY = "zeno_device_id";

// 这个「端」的标识：落地 sessionStorage，使同一浏览器的每个标签页各持不同 id
// （sessionStorage 按标签隔离，刷新保持、新标签重生）。
// 用 localStorage 会让所有标签共享同一 id，于是本地跨标签接力时，
// A 标签发出的快照在 B 标签会因 deviceId 相同被当成「自己」丢弃 —— 多标签接力从此失效。
export function getDeviceId() {
  let id = null;
  try {
    id = sessionStorage.getItem(DEVICE_KEY);
  } catch (e) {
    /* 隐私模式等禁用 sessionStorage 时降级为进程内一次性 id */
  }
  if (!id) {
    id =
      "d-" +
      Math.random().toString(36).slice(2, 10) +
      Date.now().toString(36);
    try {
      sessionStorage.setItem(DEVICE_KEY, id);
    } catch (e) {
      /* ignore */
    }
  }
  return id;
}

export class Handoff {
  // onMessage(snap)：收到他设备的快照或 clear 消息时回调。
  // opts.backend：可选的后端通道（跨平台端）。形如
  //   { isEnabled(): boolean, poll(): Promise<snap|null>,
  //     publish(snap): Promise, clear(deviceId): Promise, pollIntervalMs?: number }
  // 不传则只走本地通道（同浏览器多标签）。
  constructor(onMessage, opts = {}) {
    this.onMessage = onMessage;
    this.deviceId = getDeviceId();
    this.bc = null;

    // —— 后端通道（跨平台端）状态 ——
    this.backend = opts.backend || null;
    this._backendTimer = null;
    this._retryTimer = null;
    this._pollFails = 0; // poll 连续失败计数，达阈值后熔断（publish/clear 失败不计入）
    this._backendDisabled = false; // 熔断态：暂停轮询，一段时间后半开重试
    this._pollIntervalMs = (opts.backend && opts.backend.pollIntervalMs) || 5000;
    if (this.backend) this._startBackendPoll(this._pollIntervalMs);
    try {
      if (typeof BroadcastChannel !== "undefined") {
        this.bc = new BroadcastChannel(CHANNEL);
        this.bc.onmessage = (e) => this._dispatch(e.data);
      }
    } catch (e) {
      /* 不支持则仅靠 storage 事件 */
    }
    this._onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue) {
        try {
          this._dispatch(JSON.parse(e.newValue));
        } catch (_) {
          /* ignore */
        }
      } else {
        // newValue 为空 = 被 removeItem 清除 → 视为 clear
        this._dispatch({ type: "clear" });
      }
    };
    window.addEventListener("storage", this._onStorage);
  }

  // 过滤掉自己发的消息，其余交给上层。
  _dispatch(snap) {
    if (!snap) return;
    if (snap.deviceId && snap.deviceId === this.deviceId) return;
    this.onMessage(snap);
  }

  // ===== 后端通道（跨平台端） =====

  // 后端通道当前是否可用：已注入、未熔断、且上层判定启用（通常 = 已登录）。
  _backendOk() {
    return (
      !!this.backend && !this._backendDisabled && this.backend.isEnabled()
    );
  }

  // 包裹一次「写」类后端调用（publish/clear）：尽力而为，失败静默，不参与熔断。
  _backendCall(promise) {
    return Promise.resolve(promise).then(
      (v) => v,
      () => null
    );
  }

  // 周期轮询后端：拿到他端快照即下发；确认无活跃会话才视为 clear。
  // 关键区分「请求失败」与「确认无快照」：
  //   - poll 抛错（端点未就绪 / 网络抖动 / 401）→ 不下发 clear（否则会误撤有效横幅），
  //     累计失败达阈值则熔断，并在一段时间后半开重试（不再是永久停用）。
  //   - poll 成功但返回空 → 才是真的「对端已停」，下发 clear。
  // 服务端确认「仍活跃」时本地重盖 updatedAt，使过期判断脱离跨端时钟偏差。
  _startBackendPoll(intervalMs) {
    const tick = () => {
      if (!this._backendOk()) return;
      Promise.resolve(this.backend.poll()).then(
        (snap) => {
          this._pollFails = 0; // 成功即清零
          if (snap && (snap.sessionId || snap.deviceId)) {
            this._dispatch({ ...snap, updatedAt: Date.now() });
          } else {
            this._dispatch({ type: "clear" });
          }
        },
        (err) => {
          // 失败不下发 clear，仅计数；达阈值熔断并安排半开重试。
          this._pollFails += 1;
          if (this._pollFails >= 3 && !this._backendDisabled) {
            this._backendDisabled = true;
            this._stopBackendPoll();
            console.warn(
              "[handoff] 后端接力通道暂不可用，已暂停轮询，稍后自动重试。",
              err && err.message ? err.message : err
            );
            this._scheduleReopen();
          }
        }
      );
    };
    tick(); // 打开 App 立刻探测一次「其他端是否在跑」
    this._backendTimer = setInterval(tick, intervalMs);
  }

  // 熔断后半开：一段时间后清除熔断标记、重置计数并恢复轮询。
  // 若仍不可用会再次熔断，形成「退避重试」而非永久死亡。
  _scheduleReopen() {
    clearTimeout(this._retryTimer);
    this._retryTimer = setTimeout(() => {
      if (!this.backend) return;
      this._backendDisabled = false;
      this._pollFails = 0;
      this._startBackendPoll(this._pollIntervalMs);
    }, 60000); // 60s 后半开
  }

  _stopBackendPoll() {
    if (this._backendTimer) {
      clearInterval(this._backendTimer);
      this._backendTimer = null;
    }
  }

  // 读取当前已存在的 live 快照——storage 事件只在「变更那一刻」触发，
  // 刚打开 App 时需要主动读一次，才能发现「已经在跑」的另一台设备。
  readLatest() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const snap = JSON.parse(raw);
      if (snap.deviceId === this.deviceId) return null;
      return snap;
    } catch (e) {
      return null;
    }
  }

  // 广播一份快照（专注进行中周期性调用）。补上发送方身份与时间戳。
  publish(payload) {
    const snap = {
      ...payload,
      deviceId: this.deviceId,
      updatedAt: Date.now(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
    } catch (e) {
      /* ignore */
    }
    if (this.bc) {
      try {
        this.bc.postMessage(snap);
      } catch (e) {
        /* ignore */
      }
    }
    // 跨平台端：上报到后端共享快照（尽力而为，刷新服务端 TTL）。
    if (this._backendOk()) this._backendCall(this.backend.publish(snap));
    return snap;
  }

  // 停止广播本设备的专注（暂停/结束/被接力时调用）。
  // 仅当存储里的快照确实由本设备发出时才移除，避免误删别人刚接力发出的状态。
  clear() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const snap = JSON.parse(raw);
        if (snap.deviceId === this.deviceId) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (e) {
      /* ignore */
    }
    if (this.bc) {
      try {
        this.bc.postMessage({ type: "clear", deviceId: this.deviceId });
      } catch (e) {
        /* ignore */
      }
    }
    // 跨平台端：清除后端共享快照（尽力而为）。
    if (this._backendOk()) this._backendCall(this.backend.clear(this.deviceId));
  }

  destroy() {
    this._stopBackendPoll();
    clearTimeout(this._retryTimer);
    window.removeEventListener("storage", this._onStorage);
    if (this.bc) {
      try {
        this.bc.close();
      } catch (e) {
        /* ignore */
      }
      this.bc = null;
    }
  }
}
